import { useEffect, useRef } from 'react'
import { parseServerSentEvents } from 'parse-sse'

import { ACCESS_TOKEN, API_V2 } from 'services/environment'
import { localTransformProducts, transformLivedemosProduct } from 'utils/demoPilotSDK'
import { fetchDemoPilotAssetAsFile, guessExt, uploadFileToDsnProject } from 'utils/upload'

const CONCURRENCY = 4

async function mapWithConcurrency(items, limit, worker) {
  const results = new Array(items.length)
  let cursor = 0
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const i = cursor++
      results[i] = await worker(items[i], i)
    }
  })
  await Promise.all(runners)
  return results
}

/**
 * Stream Iron's "create products" SSE proxy (which forwards the call to
 * DemoPilot with the user's IMS cookie). Each event's `data` is a JSON
 * payload of shape: { statusCode: string, message?: string, data?: unknown }.
 *
 * Uses `parse-sse` for spec-compliant SSE framing (handles `\n\n` event
 * boundaries, multi-line `data:` fields, `\r\n` line endings, etc.).
 *
 * Resolves with the final product list on `statusCode: 'done'`,
 * rejects on `statusCode: 'error' | 'failed'` or if the stream ends without
 * a completion event.
 */
async function streamCreateProducts({ livedemosWorkspaceId, products, reportMessage }) {
  const url = `${API_V2}/demopilot/projects/${livedemosWorkspaceId}/products/sse`

  const headers = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  }
  if (typeof window !== 'undefined' && window.DSN?.originId) {
    headers['x-origin-id'] = window.DSN.originId
  }
  if (ACCESS_TOKEN) {
    headers['x-access-token'] = ACCESS_TOKEN
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: ACCESS_TOKEN ? 'omit' : 'include',
    headers,
    body: JSON.stringify(products),
  })

  if (!response.ok) {
    let details = ''
    try {
      details = await response.text()
    } catch {
      // ignore
    }
    throw new Error(
      `Live Demos request failed: ${response.status} ${response.statusText}${details ? ` — ${details}` : ''}`
    )
  }
  if (!response.body) {
    throw new Error('Live Demos stream has no response body')
  }

  for await (const event of parseServerSentEvents(response)) {
    let data
    try {
      data = JSON.parse(event.data)
    } catch {
      continue
    }

    const isErrorStatus =
      data?.statusCode === 'error' ||
      data?.statusCode === 'failed' ||
      (typeof data?.statusCode === 'number' && data.statusCode >= 400)

    if (isErrorStatus || data?.error) {
      throw new Error(data.message || data.error || 'Live Demos reported an error')
    }

    if (data?.statusCode === 'done') {
      return Array.isArray(data.data) ? data.data : []
    }

    if (typeof data?.message === 'string' && data.message) {
      reportMessage?.(data.message)
    }
  }

  throw new Error('Live Demos stream ended without a completion event')
}

/**
 * Returns true only when we can confirm the default livedemos workspace id is
 * no longer among the user's accessible workspaces (stale/removed). A stale id
 * would otherwise make the save-to-livedemos POST 404; in that case we skip the
 * save step, exactly like having no default id. Inconclusive checks (network or
 * server error) return false so the save is still attempted and the existing
 * mid-stream fallback handles any real failure.
 */
async function isLivedemosWorkspaceStale(livedemosWorkspaceId) {
  const headers = { Accept: 'application/json' }
  if (ACCESS_TOKEN) {
    headers['x-access-token'] = ACCESS_TOKEN
  }

  try {
    const response = await fetch(`${API_V2}/demopilot/projects`, {
      credentials: ACCESS_TOKEN ? 'omit' : 'include',
      headers,
    })
    if (!response.ok) return false
    const list = await response.json()
    if (!Array.isArray(list)) return false
    return !list.some((p) => p?.id === livedemosWorkspaceId)
  } catch {
    return false
  }
}

/**
 * Hook that runs the full import pipeline for products scraped by the
 * DemoPilot SDK:
 *   1. Stream them into livedemos via Iron's SSE proxy (status messages are
 *      forwarded to the dialog via `reportMessage`).
 *   2. For each created product, fetch the rendition URL, download the image
 *      bytes in the browser, and upload them to DSN S3 using the existing
 *      project upload pre-signed URL flow (same pattern as AssetsGallery's
 *      useAssetUpload).
 *   3. Map the result into Argon's product shape, substituting the DSN S3
 *      URL for the original (CORS-blocked) external image URL.
 *
 * Falls back to the local transform (keeping the original image URL) when
 * the integration is unavailable or an error occurs mid-stream.
 */
export function useScrapedProductsImport({ projectId, livedemosWorkspaceId }) {
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const uploadProductImage = async (assetId, displayName) => {
    const file = await fetchDemoPilotAssetAsFile({
      demoPilotProjectId: livedemosWorkspaceId,
      assetId,
      displayName,
    })
    if (!file || !isMountedRef.current) return null

    const extension = guessExt(file.type)
    const uploadPath = `scraped-products/${assetId}.${extension}`
    const result = await uploadFileToDsnProject(file, projectId, uploadPath)
    return result?.status === 'success' ? result.url : null
  }

  const importScrapedProducts = async (products, reportMessage = () => {}) => {
    if (!Array.isArray(products) || products.length === 0) {
      return []
    }

    if (!livedemosWorkspaceId || !projectId) {
      return localTransformProducts(products)
    }

    // Skip the livedemos save when the default workspace id is stale/removed,
    // matching the no-default-id path instead of failing the save step.
    if (await isLivedemosWorkspaceStale(livedemosWorkspaceId)) {
      return localTransformProducts(products)
    }

    try {
      reportMessage('Saving products to Live Demos workspace...')
      const created = await streamCreateProducts({
        livedemosWorkspaceId,
        products,
        reportMessage,
      })

      if (!isMountedRef.current) return []

      if (!Array.isArray(created) || created.length === 0) {
        return localTransformProducts(products)
      }

      let completed = 0
      const uploadResults = await mapWithConcurrency(created, CONCURRENCY, async (product, i) => {
        const assetId = product?.image?.id
        let imageUrl = null
        if (assetId) {
          try {
            imageUrl = await uploadProductImage(assetId, product.name)
          } catch (err) {
            console.error(`Failed to upload image for product ${product.name}:`, err)
          }
        }
        completed += 1
        reportMessage(`Uploaded ${completed} of ${created.length}`)
        return { product, imageUrl, fallback: products[i]?.image ?? null }
      })

      const results = uploadResults.map(({ product, imageUrl, fallback }) =>
        transformLivedemosProduct(product, imageUrl ?? fallback)
      )

      reportMessage('Import complete.')
      return results
    } catch (error) {
      console.error('Scraped products SSE import failed, falling back:', error)
      reportMessage?.(error instanceof Error ? error.message : 'Failed to save products to Live Demos')
      return localTransformProducts(products)
    }
  }

  return { importScrapedProducts }
}
